import React, { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ProductsContext, CartContext } from '../App';

const ProductDetail = React.memo(() => {
  const { id } = useParams();
  const { products } = useContext(ProductsContext);
  const { addToCart } = useContext(CartContext);
  const product = products.find((p) => p.id === parseInt(id));

  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Set defaults after product loads
  useEffect(() => {
    if (product) {
      setSelectedColor(product.colors[0] || '');
      
      if (product.category === 'Necklaces') {
        setSelectedSize('18"');
      } else if (product.category === 'Rings') {
        setSelectedSize('7');
      }
    }
  }, [product]);

  if (!product) return <div style={{ padding: '2rem', textAlign: 'center' }}>Product not found</div>;

  // Size options
  const sizeOptions = product.category === 'Necklaces'
    ? ['16"', '18"', '20"', '24"']
    : product.category === 'Rings'
    ? ['6', '7', '8', '9']
    : [];

  // FIXED: Get initial base price from both price and prices
  const basePrice = (() => {
    if (product.price !== undefined) {
      return product.price;  // Earrings, Bracelets
    }
    if (product.prices && typeof product.prices === 'object') {
      const firstPrice = Object.values(product.prices)[0];
      return firstPrice || 0;  // Rings, Necklaces
    }
    return 0;
  })();

  // FIXED: Dynamic price calculation with proper fallback
  const dynamicPrice = (() => {
    // If we have a size selection and prices object, try to get variant price
    if (selectedSize && product.prices && typeof product.prices === 'object') {
      const variantPrice = product.prices[selectedSize];
      if (variantPrice !== undefined) {
        return variantPrice;
      }
    }
    // Fallback to base price
    return basePrice;
  })();

  // Image selection - handles all three cases
  let currentImage = 'https://via.placeholder.com/600x800?text=No+Image';

  if (Array.isArray(product.images)) {
    // Earrings and Bracelets (array-based, color-indexed)
    const index = product.colors.indexOf(selectedColor);
    currentImage = product.images[index] || product.images[0];
  } else if (typeof product.images === 'object' && product.images !== null) {
    // Rings & Necklaces (object-based with size combination)
    const key = `${selectedColor}_${selectedSize.replace('"', '')}`;
    currentImage = product.images[key] || Object.values(product.images)[0] || currentImage;
  }

  return (
    <div style={{ display: 'flex', padding: '3rem 2rem', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', margin: '2rem' }} className="product-detail-container">
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <img
          src={currentImage}
          alt={product.name}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '600px',
            objectFit: 'contain',
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transition: 'transform 0.5s ease, box-shadow 0.3s ease',
            boxShadow: scale > 1 ? '0 0 20px rgba(212,175,55,0.5)' : 'none',
          }}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/600x800?text=Image+Not+Found';
          }}
        />
        <div className="product-buttons" style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button onClick={() => setScale(prev => Math.min(prev + 0.2, 3))}>Zoom In</button>
          <button onClick={() => setScale(prev => Math.max(prev - 0.2, 1))}>Zoom Out</button>
          <button onClick={() => setRotation(prev => prev + 90)}>Rotate</button>
        </div>
      </div>

      <div style={{ flex: 1, paddingLeft: '3rem' }} className="product-details-list">
        <h2>{product.name}</h2>
        <p className="product-detail-price">${dynamicPrice.toFixed(2)}</p>
        <p className="product-detail-description">{product.description}</p>
        
        <h4>Select Color:</h4>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {product.colors.map((color) => (
            <button
              key={color}
              onClick={() => setSelectedColor(color)}
              style={{
                padding: '0.75rem 1.5rem',
                background: selectedColor === color ? '#d4af37' : 'transparent',
                color: selectedColor === color ? '#1a1a1a' : '#d4af37',
                border: '1px solid #d4af37',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 10,
                pointerEvents: 'auto',
                minWidth: '100px',
                fontSize: '1rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
              }}
            >
              {color}
            </button>
          ))}
        </div>

        {sizeOptions.length > 0 && (
          <>
            <h4>Select Size:</h4>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {sizeOptions.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: selectedSize === size ? '#d4af37' : 'transparent',
                    color: selectedSize === size ? '#1a1a1a' : '#d4af37',
                    border: '1px solid #d4af37',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 10,
                    pointerEvents: 'auto',
                    minWidth: '80px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </>
        )}
        
        <button 
          className="add-to-cart-button" 
          onClick={() => addToCart({ ...product, selectedColor, selectedSize, price: dynamicPrice })}
        >
          Add to Cart
        </button>
        
        <div style={{ marginTop: '2rem' }}>
          <h4>Product Details</h4>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            <li><strong>Material:</strong> {product.material}</li>
            <li><strong>Weight:</strong> {product.weight}</li>
            <li><strong>Size:</strong> {product.size}</li>
            <li><strong>Additional Details:</strong> {product.additionalDetails}</li>
          </ul>
        </div>
      </div>
    </div>
  );
});

export default ProductDetail;